import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit,
    ViewChild
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { TableComponent, TableComponent as TableComponent_1 } from "@components/table/table.component";
import { cloneDeep } from "lodash";
import { forkJoin, from, of, Subject } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskColumn, RaskColumnButtonTypes } from "~app/common/components/table/models/rask-column";
import { RaskDataChanged } from "~app/common/components/table/models/rask-data-changed";

import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { AreaSelectionMarknadsomrade } from "~app/common/models/areaSelector";
import {
    Anskaffningsformer,
    Mattslag,
    MatTyper,
    RaskRoller,
    Svar,
    Transportslag
} from "~app/common/models/constants";
import { LanDTO } from "~app/common/models/distrikt/LanDTO";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { BefraktareDto } from "~app/common/models/styrtabeller/BefraktareDto";
import { StyrtabellMatningsinformationDto } from "~app/common/models/styrtabeller/StyrtabellMatningsinformationDto";
import { StyrtabellVerksamhetsomradeDTO } from "~app/common/models/styrtabeller/StyrtabellVerksamhetsomradeDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { HandelssortimentSearchService } from "~app/features/forsaljning/handelssortiment/search-panel/handelssortiment-search.service";
import { RegelverkViolService, Vardegrupper } from "~app/features/regelverk-viol/regelverk-viol.service";
import { AddOrUpdateMatningsInformationStyrningCommand } from "../command/AddOrUpdateMatningsInformationStyrningCommand";
import { DeleteMatningsInformationStyrningCommand } from "../command/DeleteMatningsInformationStyrningCommand";
import { RedovisningshanvisningValidation } from "../redovisningshanvisning/redovisningshanvisning.validation";
import { StyrtabellMatinformationViewModel } from "../regelverkViol3.models";
import { RegelverkViol3Service } from "../regelverkViol3.service";

@Component({
    selector: "rask-matinfo",
    templateUrl: "./matinfo.component.html",
    styleUrl: "./matinfo.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonComponent,
        TableComponent_1,
        ButtonComponent
    ]
})
export class MatinfoComponent implements OnInit {
    affarsformer: ISelectValue[];
    befraktare: BefraktareDto[];
    colDef: RaskColumn[];
    dataLoaded = false;
    lkd: LanDTO[];
    @Input() searchResult: StyrtabellMatinformationViewModel[];
    @ViewChild(TableComponent) tableComp: TableComponent;
    validationContext = RedovisningshanvisningValidation.init();
    verksamhetsomraden: AreaSelectionMarknadsomrade[];

    protected hasRoleProduktAdmin = false;

    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly handelssortimentSearchService = inject(HandelssortimentSearchService);
    private readonly regelverkService = inject(RegelverkViolService);
    private readonly regelverkViol3Service = inject(RegelverkViol3Service);
    private readonly authenticationService = inject(AuthenticationService);

    constructor() {
        this.commandManager = new CommandManager();
        this.hasRoleProduktAdmin = this.authenticationService.hasRole(RaskRoller.role.Produktadministrera);
    }

    add() {
        const newRow = new StyrtabellMatinformationViewModel(new StyrtabellMatningsinformationDto(), this.verksamhetsomraden, this.lkd);
        newRow.guid = GuidUtils.newGuid();
        newRow.anskaffningsform = [];
        newRow.affarsform = [];
        this.addOrUpdateCommand(newRow);

        this.searchResult.push(newRow);
        this.tableComp.refreshGrid();
    }

    canSave(): boolean {
        return this.commandManager.isDirty() && !this.validationContext.hasErrors(ValidationScopes.spara);
    }

    ngOnInit(): void {
        forkJoin({
            handelssortiment: this.handelssortimentSearchService.search({}),
            affarsformer: this.regelverkService.getVarden(Vardegrupper.affarsform),
            marknadsomraden: this.regelverkViol3Service.getMarknadsOmraden(),
            befraktare: this.regelverkViol3Service.getBefraktare(),
            lkd: this.regelverkViol3Service.getLkd()
        }).subscribe(values => {
            this.affarsformer = values.affarsformer;
            this.verksamhetsomraden = values.marknadsomraden;
            this.befraktare = values.befraktare;
            this.lkd = values.lkd;
            const dataChanged = new Subject<RaskDataChanged>();
            dataChanged.subscribe(x => {
                if (x.field === "anskaffningsform" && !x.model.anskaffningsform?.some((y: string) => Anskaffningsformer.isVirkesKop(y)) && x.model.affarsform?.length > 0) {
                    x.model.affarsform = [];
                    this.tableComp.gridApi.redrawRows();
                }
                this.update(x.model);
            });
            this.setupColumnDef(dataChanged);
            this.dataLoaded = true;
        });
    }

    save() {
        if (!this.canSave()) {
            return;
        }
        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService.addWork(work)
            .subscribe(() => {
                this.commandManager.clear();
            });
    }

    // eslint-disable-next-line sonarjs/function-return-type
    treeSelectorViewFormatter = (arg) => {
        const nodes = [];
        if (ObjectUtils.isNullOrUndefined(arg)) {
            return new Set();
        }

        arg.forEach((mo: { selected: any; text: any; partialSelected: any; children: any[] }) => {
            if (mo.selected) {
                nodes.push(mo.text);
            } else if (mo.partialSelected) {
                mo.children?.forEach((ro: { selected: any; text: any; partialSelected: any; children: any[] }) => {
                    if (ro.selected) {
                        nodes.push(ro.text);
                    } else if (ro.partialSelected) {
                        ro.children?.forEach((po: { selected: any; text: any }) => {
                            if (po.selected) {
                                nodes.push(po.text);
                            }
                        });
                    }
                });
            }
        });
        return [...new Set(nodes)].join(",");
    };

    update(viewModel: StyrtabellMatinformationViewModel) {
        this.addOrUpdateCommand(viewModel);
    }

    private addOrUpdateCommand(data: StyrtabellMatinformationViewModel): AddOrUpdateMatningsInformationStyrningCommand {
        let c = new AddOrUpdateMatningsInformationStyrningCommand((x) => x.guid = data.guid);
        const existingCommand = this.commandManager.getCommand(c);
        if (!existingCommand) {
            if (!data.id) {
                c.markAsInsert();
            }
            this.commandManager.updateCommand(c);
        }

        c = existingCommand || c;
        c.id = data.id;
        c.guid = data.guid;
        c.inmatningshandelssortiment = data.inmatningshandelssortiment;
        c.befraktareId = data.befraktare?.befraktareId;
        c.anskaffningsform = data.anskaffningsform;
        c.affarsform = data.affarsform;
        c.transportslag = data.transportslag;
        c.verksamhetsomraden = this.getSelectedVerksamhetsomraden(data.treeselectorVerksamhetsomradeSelectItems);
        c.mattslag = data.mattslag;
        c.andraMattslag = data.andraMattslag;
        c.kvalitetsklassningKravs = data.kvalitetsklassningKravs;
        c.stockmatningKravs = data.stockmatningKravs;
        c.prioriteradMatmetod = data.prioriteradMatmetod;
        return c;
    }

    private copy(matinfo: StyrtabellMatinformationViewModel) {
        const newData = cloneDeep(matinfo);
        newData.id = undefined;
        newData.guid = GuidUtils.newGuid();
        this.addOrUpdateCommand(newData);
        this.searchResult.push(newData);
        this.tableComp.refreshGrid();
    }

    private getSelectedVerksamhetsomraden(selectionItems: SelectionItem[]): StyrtabellVerksamhetsomradeDTO[] {
        const selectedValues = this.regelverkService.getSelectedVerksamhetsomraden(selectionItems);
        return this.regelverkService.mapToSelectedVerksamhetsomrade(selectedValues, this.verksamhetsomraden);
    }

    private remove(matinfo: StyrtabellMatinformationViewModel) {
        const index = this.searchResult.findIndex(obj => obj.guid === matinfo.guid);
        if (index > -1) {
            this.searchResult.splice(index, 1);
        }

        const cmd = new DeleteMatningsInformationStyrningCommand(c => {
            c.guid = matinfo.guid;
        });
        this.commandManager.updateCommand(cmd);
        this.tableComp.refreshGrid();
    }

    private setupColumnDef(dataChanged: Subject<RaskDataChanged>) {
        this.colDef = [
            {
                editable: false,
                field: " ",
                type: "button",
                onClick: (m: StyrtabellMatinformationViewModel) => this.remove(m),
                filter: false,
                text: "",
                minWidth: 30,
                maxWidth: 30,
                disabled: !this.hasRoleProduktAdmin,
                buttonType: RaskColumnButtonTypes.delete
            },
            {
                editable: false,
                field: "  ",
                type: "button",
                onClick: (m: StyrtabellMatinformationViewModel) => this.copy(m),
                filter: false,
                text: "",
                minWidth: 30,
                maxWidth: 30,
                disabled: !this.hasRoleProduktAdmin,
                buttonType: RaskColumnButtonTypes.copy
            },
            {
                headerName: "Inmätningshandelssortiment *",
                field: "inmatningshandelssortiment",
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                minWidth: 200,
                maxWidth: 250,
                rule: this.validationContext.current.Inmatningshandelssortiment,
                dataChanged: dataChanged
            },
            {
                headerName: "Anskaffningsform",
                field: "anskaffningsform",
                editable: this.hasRoleProduktAdmin,
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => data.anskaffningsform.some((x: string) => x.toLowerCase().indexOf(filterText.toLowerCase()) >= 0)
                },
                sortable: false,
                type: "multiselector",
                minWidth: 200,
                maxWidth: 250,
                values: of(Anskaffningsformer.AllaRegelverkViol3),
                formatter: (arg) => arg,
                viewFormatter: (arg) => arg ? arg.join(",") : undefined,
                dataChanged: dataChanged
            },
            {
                readonly: (row) => !row.anskaffningsform?.some((x: string) => Anskaffningsformer.isVirkesKop(x)),
                headerName: "Affärsform",
                field: "affarsform",
                editable: this.hasRoleProduktAdmin,
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => data.affarsform.some((x: string) => x.toLowerCase().indexOf(filterText.toLowerCase()) >= 0)
                },
                sortable: false,
                type: "multiselector",
                minWidth: 200,
                maxWidth: 250,
                multiselectorBindLabel: "namn",
                multiselectorBindValue: "kod",
                values: of(this.affarsformer),
                viewFormatter: (arg) => arg ? arg.join(",") : undefined,
                dataChanged: dataChanged
            },
            {
                headerName: "Befraktare",
                field: "befraktare",
                values: this.befraktare,
                formatter: (arg) => arg ? arg : undefined,
                viewFormatter: (arg: BefraktareDto) => arg ? `${arg.namn} (${arg.huvudkod}-${arg.internummer})` : undefined,
                type: "selector",
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => {
                        const value = `${data.befraktare.huvudkod.toLowerCase()}-${data.befraktare.internummer.toLowerCase()}`;
                        return value.indexOf(filterText.toLowerCase()) >= 0;
                    }
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                sortComparator: (valueA: BefraktareDto, valueB: BefraktareDto) => {
                    if (ObjectUtils.isNullOrUndefined(valueA) || ObjectUtils.isNullOrUndefined(valueB) || valueA.huvudkod === valueB.huvudkod) {
                        return 0;
                    }
                    return (Number(valueA.huvudkod) > Number(valueB.huvudkod)) ? 1 : -1;
                },
                minWidth: 120,
                maxWidth: 170,
                dataChanged: dataChanged,
                tooltipGenerator: (row: StyrtabellMatinformationViewModel) =>
                    row.befraktare ? `${row.befraktare.namn} (${row.befraktare.huvudkod}-${row.befraktare.internummer})` : undefined
            },
            {
                headerName: "Transportslag",
                field: "transportslag",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Transportslag.allaMedTransport),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 50,
                maxWidth: 100,
                dataChanged: dataChanged
            },
            {
                headerName: "MO/RO/PO",
                field: "treeselectorVerksamhetsomradeSelectItems",
                editable: this.hasRoleProduktAdmin,
                filter: false,
                sortable: false,
                type: "treeselector",
                minWidth: 100,
                maxWidth: 200,
                values: (arg: { treeselectorVerksamhetsomradeSelectItems: any }) => arg.treeselectorVerksamhetsomradeSelectItems,
                comparePredicate: (a, b) => a.value === b,
                viewFormatter: this.treeSelectorViewFormatter,
                dataChanged: dataChanged
            },
            {
                headerName: "Måttslag",
                field: "mattslag",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Mattslag.AllaViol3Mattslag),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 50,
                maxWidth: 100,
                dataChanged: dataChanged
            },
            {
                headerName: "Andra måttslag",
                field: "andraMattslagString",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Svar.alla),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 100,
                maxWidth: 150,
                dataChanged: dataChanged
            },
            {
                headerName: "Stockmätning",
                field: "stockmatningString",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Svar.alla),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 100,
                maxWidth: 150,
                dataChanged: dataChanged
            },
            {
                headerName: "Kvalitetsklassning krävs",
                field: "kvalitetsklassningKravsString",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Svar.alla),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 100,
                maxWidth: 200,
                dataChanged: dataChanged
            },
            {
                headerName: "Prioriterad mätmetod",
                field: "prioriteradMatmetod",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(MatTyper.alla),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 100,
                maxWidth: 200,
                dataChanged: dataChanged
            }
        ] as unknown as RaskColumn[];
    }
}
